import React, { useState, useEffect, useRef } from 'react'
import { GridLayout, Layout, LayoutItem } from 'react-grid-layout'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'
import { ChartData } from './hooks/useChartData'
import ChartPanel from './ChartPanel'

const DRAG_HANDLE = '.drag-handle'

export type { LayoutItem }

interface Props {
    charts: ChartData[]
    layout: LayoutItem[]
    setLayout: (l: LayoutItem[]) => void
    onRemove: (id: string) => void
    onResizeOne: (id: string, w: number) => void
    enabledStats: Set<string>
    getAxisDomain: (chartId: string) => ['auto' | number, 'auto' | number]
    isMobile?: boolean
    periodoPendente?: boolean
}

export function defaultItem(id: string, index: number): LayoutItem {
    const col = (index % 2) * 6
    const row = Math.floor(index / 2) * 2
    return { i: id, x: col, y: row, w: 6, h: 2, minW: 3, minH: 2 }
}

const ChartGrid: React.FC<Props> = ({ charts, layout, setLayout, onRemove, onResizeOne, enabledStats, getAxisDomain, isMobile = false, periodoPendente = false }) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const [width, setWidth] = useState(600)

    useEffect(() => {
        if (!containerRef.current) return
        const obs = new ResizeObserver(entries => {
            const w = entries[0]?.contentRect.width
            if (w) setWidth(w)
        })
        obs.observe(containerRef.current)
        return () => obs.disconnect()
    }, [])

    // Garante que cada chart tem um layout item
    const fullLayout: LayoutItem[] = charts.map((c, i) => {
        const existing = layout.find(l => l.i === c.id)
        return existing ?? defaultItem(c.id, i)
    })

    // No mobile, sempre coluna única empilhada — sem drag/resize.
    // Respeita a ordem (y, x) do layout atual, para que "reorganizar" também funcione no mobile.
    const renderLayout: LayoutItem[] = isMobile
        ? (() => {
            const sorted = [...fullLayout].sort((a, b) => a.y - b.y || a.x - b.x)
            let y = 0
            return sorted.map(item => {
                const placed = { ...item, x: 0, w: 12, y }
                y += item.h
                return placed
            })
        })()
        : fullLayout

    const onLayoutChange = (newLayout: Layout) => {
        setLayout([...newLayout] as LayoutItem[])
    }

    // Largura atual de um chart específico
    const currentW = (id: string): number => {
        const item = fullLayout.find(l => l.i === id)
        return item?.w ?? 6
    }

    return (
        <div ref={containerRef} style={{ width: '100%', minHeight: '100%' }}>
            <style>{`.react-grid-item.react-grid-placeholder { background: #92B258 !important; }`}</style>
            {charts.length === 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', color: 'rgba(255,255,255,0.25)', fontFamily: 'Nunito, sans-serif', fontSize: 14, gap: 12 }}>
                    <span style={{ fontSize: 40 }}>📊</span>
                    <span>{isMobile ? 'Adicione gráficos pelo botão +' : 'Adicione gráficos usando o painel à esquerda'}</span>
                </div>
            ) : (
                <GridLayout
                    layout={renderLayout}
                    width={width}
                    gridConfig={{ cols: 12, rowHeight: 150, margin: [12, 12] as [number, number], containerPadding: null }}
                    dragConfig={{ handle: DRAG_HANDLE, enabled: !isMobile, bounded: false, threshold: 3 }}
                    resizeConfig={{ enabled: !isMobile }}
                    onLayoutChange={isMobile ? () => {} : onLayoutChange}
                >
                    {charts.map(c => (
                        <div key={c.id}>
                            <ChartPanel
                                chart={c}
                                onRemove={onRemove}
                                dragHandle="drag-handle"
                                currentW={currentW(c.id)}
                                onResizeOne={onResizeOne}
                                enabledStats={enabledStats}
                                axisDomain={getAxisDomain(c.id)}
                                isMobile={isMobile}
                                periodoPendente={periodoPendente}
                            />
                        </div>
                    ))}
                </GridLayout>
            )}
        </div>
    )
}

export default ChartGrid
